import { useState, useEffect, useRef } from 'react'
import { X, Search, Plus } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useConnectionRecovered } from '../../hooks/useConnectionRecovered'

/**
 * Generic multi-entity picker — searches a Supabase table and lets the user
 * pick multiple items. Stores an array of { id, label } objects.
 *
 * Props:
 *  - value:       Array of { id, label } — current selections
 *  - onChange:     (newArray) => void
 *  - table:       Supabase table to search (e.g. 'contacts', 'projects')
 *  - searchCols:  Array of column names to ilike search (e.g. ['first_name','last_name'])
 *  - labelFn:     (row) => string — how to render a row's label
 *  - placeholder:  input placeholder text
 *  - selectCols:  columns to fetch (default '*')
 *  - limit:       max results (default 8)
 *  - onCreate:    (query) => Promise<{ id, label }> — if provided, shows "Create [query]" when no results
 */
export default function MultiEntityPicker({
  value = [],
  onChange,
  table,
  searchCols = ['name'],
  labelFn = (r) => r.name || r.title || r.id,
  placeholder = 'Search...',
  selectCols = '*',
  limit = 8,
  onCreate,
}) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [open, setOpen] = useState(false)
  const [recoverTick, setRecoverTick] = useState(0)
  const debounceRef = useRef(null)
  const containerRef = useRef(null)

  // Re-run the current search after the connection un-wedges
  useConnectionRecovered(() => { if (query.trim()) setRecoverTick(t => t + 1) })

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Debounced search — name-aware for first_name/last_name columns
  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const parts = query.trim().split(/\s+/).filter(Boolean)
        const isNameSearch = searchCols.includes('first_name') && searchCols.includes('last_name')
        let dbFilter, clientFilter

        if (isNameSearch && parts.length > 1) {
          // Name-aware: first part → first_name, skip middle initials only if not last part
          const firstName = parts[0]
          const afterFirst = parts.slice(1)
          const rest = afterFirst.filter((p, i) => {
            const isShort = p.length === 1 || (p.length === 2 && p.endsWith('.'))
            return !isShort || i === afterFirst.length - 1
          })
          const lastName = rest.join(' ')
          dbFilter = `first_name.ilike.%${firstName}%`
          clientFilter = (r) => {
            const matchFirst = (r.first_name || '').toLowerCase().includes(firstName.toLowerCase())
            const matchLast = lastName ? (r.last_name || '').toLowerCase().includes(lastName.toLowerCase()) : true
            return matchFirst && matchLast
          }
        } else {
          // Single word or non-name columns: OR across all columns
          dbFilter = parts.flatMap(w => searchCols.map(c => `${c}.ilike.%${w}%`)).join(',')
          clientFilter = null
        }

        const { data, error } = await supabase
          .from(table)
          .select(selectCols)
          .or(dbFilter)
          .limit(limit)
        if (error) throw error

        // Deduplicate and apply filter
        const seen = new Set()
        setResults((data || []).filter(r => {
          if (seen.has(r.id)) return false
          seen.add(r.id)
          return clientFilter ? clientFilter(r) : true
        }))
      } catch (err) {
        console.error('MultiEntityPicker search error:', err)
        setResults([])
      }
      setSearching(false)
    }, 250)
    return () => clearTimeout(debounceRef.current)
  }, [query, table, searchCols.join(','), selectCols, limit, recoverTick])

  const [creating, setCreating] = useState(false)
  const safeValue = Array.isArray(value) ? value : []
  const selectedIds = new Set(safeValue.map(v => v.id))

  const add = (row) => {
    if (selectedIds.has(row.id)) return
    onChange([...safeValue, { ...row, id: row.id, label: labelFn(row) }])
    setQuery('')
    setResults([])
  }

  const handleCreate = async () => {
    if (!onCreate || !query.trim() || creating) return
    setCreating(true)
    try {
      const newItem = await onCreate(query.trim())
      if (newItem) {
        onChange([...safeValue, newItem])
        setQuery('')
        setResults([])
        setOpen(false)
      }
    } catch (e) {
      console.error('Failed to create:', e)
    } finally {
      setCreating(false)
    }
  }

  const remove = (id) => {
    onChange(safeValue.filter(v => v.id !== id))
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Selected chips */}
      {safeValue.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {safeValue.map(v => (
            <span key={v.id} className="inline-flex items-center gap-1 px-2 py-0.5 bg-navy-50 border border-navy-200 text-navy-700 rounded-full text-xs">
              {v.label}
              <button type="button" onClick={() => remove(v.id)} className="hover:text-red-600 transition-colors">
                <X size={10} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-warm-400" />
        <input
          className="input-field pl-8 text-sm"
          placeholder={placeholder}
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
        />
      </div>

      {/* Dropdown */}
      {open && query.trim() && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-warm-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {searching ? (
            <div className="px-3 py-2 text-xs text-warm-400">Searching...</div>
          ) : (
            <>
              {results.filter(r => !selectedIds.has(r.id)).map(r => (
                <button
                  key={r.id}
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-warm-50 transition-colors flex items-center gap-2"
                  onMouseDown={(e) => { e.preventDefault(); add(r) }}
                >
                  <Plus size={12} className="text-warm-400" />
                  {labelFn(r)}
                </button>
              ))}
              {results.filter(r => !selectedIds.has(r.id)).length === 0 && !onCreate && (
                <div className="px-3 py-2 text-xs text-warm-400">No results</div>
              )}
              {onCreate && results.filter(r => !selectedIds.has(r.id)).length === 0 && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-navy-50 transition-colors flex items-center gap-2 text-navy-600 font-medium"
                  onMouseDown={(e) => { e.preventDefault(); handleCreate() }}
                >
                  <Plus size={12} />
                  {creating ? 'Creating...' : `Create "${query.trim()}"`}
                </button>
              )}
              {onCreate && results.filter(r => !selectedIds.has(r.id)).length > 0 && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 text-xs hover:bg-navy-50 transition-colors flex items-center gap-2 text-navy-500 border-t border-warm-100"
                  onMouseDown={(e) => { e.preventDefault(); handleCreate() }}
                >
                  <Plus size={10} />
                  {creating ? 'Creating...' : `Create "${query.trim()}" as new`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Multi-user picker — specifically for user_profiles table
 */
export function MultiUserPicker({ value = [], onChange, placeholder = 'Assign team members...' }) {
  return (
    <MultiEntityPicker
      value={value}
      onChange={onChange}
      table="user_profiles"
      searchCols={['full_name', 'email']}
      labelFn={(r) => r.full_name || r.email}
      placeholder={placeholder}
      selectCols="id, full_name, email"
    />
  )
}
